import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { MyHomeReturn, MyProfileReturn } from '../clients/rust-api-client';
import { memberProfileService } from '../clients/ApiClients';
import {
  generateInitials,
  getToken,
  subscribeToToken,
} from '../utils/TokenUtils';
import { apiErrorMessage } from '../utils/apiErrors';

/**
 * The signed-in member, loaded from `GET /member-portal/me`.
 *
 * This has to be a fetch rather than a token decode: `MemberClaims`
 * carries ids and nothing human — no name, no email, no addresses. Every
 * screen that wants to greet the member or label a home needs this.
 */
interface MemberContextType {
  member: MyProfileReturn | null;
  /** The member's homes, with addresses and HOA names. May be empty. */
  homes: MyHomeReturn[];
  /**
   * The home currently in view. Most members have exactly one; the
   * backend models `home_ids` as a list, so more than one is legitimate
   * and the UI shows a switcher in that case.
   */
  selectedHome: MyHomeReturn | null;
  selectHome: (homeId: string) => void;
  /** Display name, preferring what the member asked to be called. */
  displayName: string;
  initials: string;
  loading: boolean;
  /** Set when the profile could not be loaded. */
  error: string | null;
  reload: () => void;
}

const MemberContext = createContext<MemberContextType | undefined>(undefined);

export const MemberProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [member, setMember] = useState<MyProfileReturn | null>(null);
  const [selectedHomeId, setSelectedHomeId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState<number>(0);

  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  // Subscribe rather than read inside the effect. Storage is not reactive,
  // and this effect only depended on `reloadCount`, so the profile was
  // fetched once at mount and never again: signing out and back in as a
  // different member left the previous member's name and homes on screen.
  const token = useSyncExternalStore(subscribeToToken, getToken);

  useEffect(() => {
    // No token means the router is about to send us to /login; there is
    // nothing to fetch and a 401 here would only race that redirect.
    if (!token) {
      setMember(null);
      setSelectedHomeId(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await memberProfileService.getMyProfile();
        if (cancelled) return;
        setMember(data);
        setSelectedHomeId((current) => {
          // Keep the current selection if it is still one of the
          // member's homes; otherwise fall back to the first.
          const stillValid =
            current && data.homes.some((h) => h.home_id === current);
          return stillValid ? current : (data.homes[0]?.home_id ?? null);
        });
      } catch (err) {
        if (cancelled) return;
        // A 401 is already handled by the response interceptor, which
        // redirects to /login. Anything else is worth showing.
        setError(apiErrorMessage(err, "Couldn't load your account."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [token, reloadCount]);

  const value = useMemo<MemberContextType>(() => {
    const homes = member?.homes ?? [];
    const displayName = member
      ? (member.preferred_name || member.first_name) +
        (member.last_name ? ` ${member.last_name}` : '')
      : '';

    return {
      member,
      homes,
      selectedHome:
        homes.find((h) => h.home_id === selectedHomeId) ?? homes[0] ?? null,
      selectHome: setSelectedHomeId,
      displayName,
      initials: displayName ? generateInitials(displayName) : '',
      loading,
      error,
      reload,
    };
  }, [member, selectedHomeId, loading, error, reload]);

  return (
    <MemberContext.Provider value={value}>{children}</MemberContext.Provider>
  );
};

export const useMember = (): MemberContextType => {
  const context = useContext(MemberContext);
  if (context === undefined) {
    throw new Error('useMember must be used within a MemberProvider');
  }
  return context;
};

/** Format one of the member's homes as a single address line. */
export const formatHomeAddress = (home: MyHomeReturn): string => {
  const street = home.street2 ? `${home.street} ${home.street2}` : home.street;
  return `${street}, ${home.city}, ${home.state} ${home.zip}`;
};
